import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { signaturesAPI } from '../../lib/api';
import { SignaturePad } from './SignaturePad';
import { Users2, Loader2, CheckCircle2, Clock, Copy, Mail, PenLine, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const fmt = (iso) => { if (!iso) return ''; try { return new Date(iso).toLocaleString('de-DE'); } catch { return iso; } };

export const TwoPartySignatureDialog = ({ open, onOpenChange, doc, employeeName = '', existingWorkflow = null, onChanged }) => {
  const [wf, setWf] = useState(existingWorkflow);
  const [busy, setBusy] = useState(false);
  const padRef = useRef(null);
  const [place, setPlace] = useState('');

  const [parties, setParties] = useState([
    { role_label: 'Arbeitgeber', signer_name: '', method: 'in_app', email: '' },
    { role_label: 'Arbeitnehmer', signer_name: employeeName, method: 'link', email: '' },
  ]);

  useEffect(() => {
    if (open) {
      setWf(existingWorkflow);
      setPlace('');
      setParties([
        { role_label: 'Arbeitgeber', signer_name: '', method: 'in_app', email: '' },
        { role_label: 'Arbeitnehmer', signer_name: employeeName, method: 'link', email: '' },
      ]);
    }
  }, [open, existingWorkflow, employeeName]);

  const setParty = (i, patch) => setParties((ps) => ps.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));

  const reload = async (id) => {
    try { const { data } = await signaturesAPI.getWorkflow(id); setWf(data); onChanged?.(); } catch {}
  };

  const create = async () => {
    for (const p of parties) {
      if (!p.signer_name.trim()) { toast.error(`Bitte Name für „${p.role_label}" angeben`); return; }
      if (p.method === 'link' && !p.email.trim()) { toast.error(`Bitte E-Mail für „${p.role_label}" (Link-Versand) angeben`); return; }
    }
    setBusy(true);
    try {
      const { data } = await signaturesAPI.createWorkflow({
        document_id: doc.id, base_url: window.location.origin,
        parties: parties.map((p) => ({ role_label: p.role_label.trim(), signer_name: p.signer_name.trim(), method: p.method, email: p.email.trim() || null })),
      });
      setWf(data);
      toast.success('Signatur-Workflow gestartet');
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Workflow konnte nicht gestartet werden'); }
    finally { setBusy(false); }
  };

  const signStep = async () => {
    const sig = padRef.current?.getSignaturePng();
    if (!sig) { toast.error('Bitte unterschreiben'); return; }
    setBusy(true);
    try {
      const { data } = await signaturesAPI.signWorkflowStep(wf.id, { signature_image_base64: sig, place: place.trim() || null });
      setWf(data.workflow); onChanged?.();
      toast.success('Schritt signiert');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Signieren fehlgeschlagen'); }
    finally { setBusy(false); }
  };

  const copyLink = (party) => {
    const url = `${window.location.origin}/signieren/${party.token}`;
    navigator.clipboard.writeText(url).then(() => toast.success('Link kopiert')).catch(() => window.prompt('Link:', url));
  };

  const remind = async () => {
    setBusy(true);
    try { const { data } = await signaturesAPI.remindWorkflow(wf.id, { base_url: window.location.origin }); toast.success(data.message); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Erinnerung fehlgeschlagen'); }
    finally { setBusy(false); }
  };

  const cancel = async () => {
    if (!window.confirm('Signatur-Workflow wirklich abbrechen?')) return;
    setBusy(true);
    try { await signaturesAPI.cancelWorkflow(wf.id); toast.success('Workflow abgebrochen'); await reload(wf.id); }
    catch (e) { toast.error('Abbrechen fehlgeschlagen'); }
    finally { setBusy(false); }
  };

  const currentIdx = wf?.current_index;
  const currentParty = wf && wf.status === 'in_progress' ? wf.parties[currentIdx] : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="two-party-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Users2 className="h-5 w-5 text-primary" /> Zwei-seitige Signatur</DialogTitle>
          <DialogDescription className="truncate">{doc?.title}</DialogDescription>
        </DialogHeader>

        {!wf ? (
          // ---- Setup ----
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground">Beide Parteien unterschreiben nacheinander dasselbe Dokument. Reihenfolge: von oben nach unten.</p>
            {parties.map((p, i) => (
              <div key={i} className="rounded-lg border border-border/60 p-3 space-y-3" data-testid={`party-setup-${i}`}>
                <div className="flex items-center gap-2 text-sm font-medium"><Badge variant="outline">{i + 1}</Badge>
                  <Input value={p.role_label} onChange={(e) => setParty(i, { role_label: e.target.value })} className="h-8 max-w-[180px]" data-testid={`party-role-${i}`} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1"><Label className="text-xs">Name *</Label><Input value={p.signer_name} onChange={(e) => setParty(i, { signer_name: e.target.value })} className="h-8" data-testid={`party-name-${i}`} /></div>
                  <div className="space-y-1"><Label className="text-xs">Signiert per</Label>
                    <Select value={p.method} onValueChange={(v) => setParty(i, { method: v })}>
                      <SelectTrigger className="h-8" data-testid={`party-method-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="in_app">In der App</SelectItem>
                        <SelectItem value="link">Per Link / E-Mail</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {p.method === 'link' && (
                  <div className="space-y-1"><Label className="text-xs">E-Mail (für Link-Versand)</Label><Input type="email" value={p.email} onChange={(e) => setParty(i, { email: e.target.value })} className="h-8" placeholder="person@example.de" data-testid={`party-email-${i}`} /></div>
                )}
              </div>
            ))}
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
              <Button onClick={create} disabled={busy} data-testid="wf-create-btn">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Workflow starten</Button>
            </DialogFooter>
          </div>
        ) : (
          // ---- Status ----
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              {wf.status === 'completed' && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200" data-testid="wf-status"><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Abgeschlossen</Badge>}
              {wf.status === 'in_progress' && <Badge className="bg-amber-100 text-amber-700 border-amber-200" data-testid="wf-status"><Clock className="h-3.5 w-3.5 mr-1" /> In Bearbeitung</Badge>}
              {wf.status === 'cancelled' && <Badge className="bg-red-100 text-red-700 border-red-200" data-testid="wf-status"><XCircle className="h-3.5 w-3.5 mr-1" /> Abgebrochen</Badge>}
            </div>

            <div className="space-y-2">
              {wf.parties.map((p, i) => {
                const isCurrent = wf.status === 'in_progress' && i === currentIdx;
                return (
                  <div key={i} className={`rounded-lg border p-3 ${isCurrent ? 'border-primary/50 bg-primary/5' : 'border-border/60'}`} data-testid={`party-row-${i}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{p.role_label}: {p.signer_name}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {p.method === 'link' ? 'Per Link' : 'In der App'}
                          {p.email ? ` · ${p.email}` : ''}
                          {p.signed_at ? ` · signiert ${fmt(p.signed_at)}` : ''}
                        </p>
                      </div>
                      {p.status === 'signed'
                        ? <Badge variant="outline" className="bg-emerald-100 text-emerald-700 border-emerald-200 shrink-0" data-testid={`party-signed-${i}`}><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Signiert</Badge>
                        : isCurrent ? <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-200 shrink-0">Am Zug</Badge>
                        : <Badge variant="outline" className="shrink-0">Wartet</Badge>}
                    </div>
                    {isCurrent && p.method === 'link' && (
                      <div className="mt-2 flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => copyLink(p)} data-testid={`copy-link-${i}`}><Copy className="h-4 w-4 mr-1" /> Link kopieren</Button>
                        {p.email && <Button size="sm" variant="outline" onClick={remind} disabled={busy} data-testid={`remind-${i}`}><Mail className="h-4 w-4 mr-1" /> Erinnerung</Button>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {currentParty && currentParty.method === 'in_app' && (
              <div className="rounded-lg border border-primary/40 p-3 space-y-3" data-testid="in-app-sign-block">
                <p className="text-sm font-medium flex items-center gap-2"><PenLine className="h-4 w-4 text-primary" /> Jetzt signieren als {currentParty.role_label}</p>
                <SignaturePad ref={padRef} defaultName={currentParty.signer_name} />
                <div className="space-y-1"><Label className="text-xs">Ort (optional)</Label><Input value={place} onChange={(e) => setPlace(e.target.value)} className="h-8" data-testid="wf-place" /></div>
                <Button className="w-full" onClick={signStep} disabled={busy} data-testid="wf-sign-btn">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Rechtsverbindlich signieren</Button>
              </div>
            )}

            {wf.status === 'completed' && (
              <p className="text-sm text-emerald-700 dark:text-emerald-400">Alle Parteien haben unterschrieben. Das signierte Dokument liegt in der Akte unter „Signierte Dokumente".</p>
            )}

            <DialogFooter className="gap-2 sm:justify-between">
              {wf.status === 'in_progress'
                ? <Button variant="ghost" className="text-destructive" onClick={cancel} disabled={busy} data-testid="wf-cancel-btn"><XCircle className="h-4 w-4 mr-1" /> Abbrechen</Button>
                : <span />}
              <Button variant="outline" onClick={() => onOpenChange(false)}>Schließen</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
